import { useEffect, useCallback } from 'react';
import { useSettingsStore, type SettingsTab } from '../store/settings';
import { ProfileTab } from './settings/ProfileTab';
import { CreditsTab } from './settings/CreditsTab';
import { SubscriptionTab } from './settings/SubscriptionTab';
import { N8nConnectionTab } from './settings/N8nConnectionTab';
import { DangerZoneTab } from './settings/DangerZoneTab';

function XMarkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
    </svg>
  );
}

function CoinsIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 6.375c0 2.278-3.694 4.125-8.25 4.125S3.75 8.653 3.75 6.375m16.5 0c0-2.278-3.694-4.125-8.25-4.125S3.75 4.097 3.75 6.375m16.5 0v11.25c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125V6.375m16.5 0v3.75m-16.5-3.75v3.75m16.5 0v3.75C20.25 16.153 16.556 18 12 18s-8.25-1.847-8.25-4.125v-3.75m16.5 0c0 2.278-3.694 4.125-8.25 4.125s-8.25-1.847-8.25-4.125" />
    </svg>
  );
}

function CreditCardIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 002.25-2.25V6.75A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25v10.5A2.25 2.25 0 004.5 19.5z" />
    </svg>
  );
}

function LinkIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
    </svg>
  );
}

function ExclamationTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
    </svg>
  );
}

function ArrowUpIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18" />
    </svg>
  );
}

interface TabItem {
  id: SettingsTab;
  label: string;
  icon: React.FC<{ className?: string }>;
}

const userTabs: TabItem[] = [
  { id: 'profile', label: 'Profile', icon: UserIcon },
];

const workspaceTabs: TabItem[] = [
  { id: 'credits', label: 'Credits', icon: CoinsIcon },
  { id: 'subscription', label: 'Subscription', icon: CreditCardIcon },
];

const connectionsTabs: TabItem[] = [
  { id: 'n8n-connection', label: 'n8n Connection', icon: LinkIcon },
];

const dangerTabs: TabItem[] = [
  { id: 'danger-zone', label: 'Danger Zone', icon: ExclamationTriangleIcon },
];

export function SettingsModal() {
  const { isSettingsModalOpen, activeSettingsTab, closeSettingsModal, setActiveSettingsTab } = useSettingsStore();

  // Handle Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeSettingsModal();
      }
    },
    [closeSettingsModal]
  );

  useEffect(() => {
    if (isSettingsModalOpen) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isSettingsModalOpen, handleKeyDown]);

  if (!isSettingsModalOpen) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      closeSettingsModal();
    }
  };

  const renderTabButton = (tab: TabItem) => {
    const isActive = activeSettingsTab === tab.id;
    const Icon = tab.icon;
    return (
      <button
        key={tab.id}
        onClick={() => setActiveSettingsTab(tab.id)}
        className={`flex w-full items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
          isActive
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
      >
        <Icon className={`h-5 w-5 ${isActive ? 'text-gray-900' : 'text-gray-400'}`} />
        {tab.label}
      </button>
    );
  };

  const renderSectionLabel = (label: string) => (
    <div className="px-4 py-2 text-xs font-medium text-gray-400 uppercase tracking-wide">
      {label}
    </div>
  );

  const renderTabContent = () => {
    switch (activeSettingsTab) {
      case 'profile':
        return <ProfileTab />;
      case 'credits':
        return <CreditsTab />;
      case 'subscription':
        return <SubscriptionTab />;
      case 'n8n-connection':
        return <N8nConnectionTab />;
      case 'danger-zone':
        return <DangerZoneTab />;
      default:
        return <ProfileTab />;
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
      onClick={handleBackdropClick}
    >
      <div className="relative flex flex-col md:flex-row w-full h-full md:max-w-5xl md:h-[700px] md:rounded-2xl bg-white shadow-xl overflow-hidden animate-fade-in">
        {/* Close button — top right of modal */}
        <button
          onClick={closeSettingsModal}
          className="absolute top-4 right-4 z-10 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>

        {/* Settings Sidebar / Tab Bar */}
        <div className="w-full md:w-60 flex-shrink-0 flex flex-col bg-gray-50 border-b md:border-b-0 md:border-r border-gray-200">
          {/* Header */}
          <div className="px-4 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Settings</h2>
          </div>

          {/* Tab Navigation — horizontal scroll on mobile, vertical list on desktop */}
          <nav className="flex md:flex-col overflow-x-auto md:overflow-x-visible md:overflow-y-auto md:flex-1 md:py-4 md:space-y-1">
            {/* Section labels hidden on mobile */}
            <div className="hidden md:block">{renderSectionLabel('User')}</div>
            <div className="flex md:flex-col md:px-2 md:space-y-1">
              {userTabs.map(renderTabButton)}
            </div>

            <div className="hidden md:block md:pt-4">{renderSectionLabel('Workspace')}</div>
            <div className="flex md:flex-col md:px-2 md:space-y-1">
              {workspaceTabs.map(renderTabButton)}
            </div>

            <div className="hidden md:block md:pt-4">{renderSectionLabel('Connections')}</div>
            <div className="flex md:flex-col md:px-2 md:space-y-1">
              {connectionsTabs.map(renderTabButton)}
            </div>

            <div className="hidden md:block md:pt-4">{renderSectionLabel('Danger Zone')}</div>
            <div className="flex md:flex-col md:px-2 md:space-y-1">
              {dangerTabs.map(renderTabButton)}
            </div>
          </nav>

          {/* Upgrade Plan Link (desktop only) */}
          <div className="hidden md:block p-4 border-t border-gray-200">
            <button
              onClick={() => setActiveSettingsTab('subscription')}
              className="w-full justify-center inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
            >
              Upgrade plan
              <ArrowUpIcon className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Right Content Panel */}
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-white">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
}
